import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Music } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { BandStatusBadge } from '@/components/Badges';

const EMPTY = {
  name: '', short_description: '', full_description: '', city: '', state: '', members: '',
  instagram_url: '', youtube_url: '', spotify_url: '', website_url: '', logo_url: '',
  status: 'participante', internal_notes: '',
};

export default function AdminBands() {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/admin/bands');
    setBands(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome obrigatório'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/bands/${editing.id}`, form);
        toast.success('Banda atualizada');
      } else {
        await api.post('/admin/bands', form);
        toast.success('Banda criada');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      const r = await api.delete(`/admin/bands/${confirmDel.id}`);
      toast.success(r.data.soft_deleted ? 'Banda inativada (tem histórico).' : 'Banda excluída');
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const renderBandsList = () => {
    if (loading) return <div className="text-muted-foreground">Carregando…</div>;
    if (bands.length === 0) {
      return (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            <Music className="w-8 h-8 mx-auto mb-2" />
            Nenhuma banda cadastrada.
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {bands.map((b) => (
              <div key={b.id} className="p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded bg-[#0b0c0f] flex items-center justify-center border border-border overflow-hidden">
                  {b.logo_url
                    ? <img src={b.logo_url} alt={b.name} className="w-full h-full object-cover" />
                    : <Music className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-white">{b.name}</div>
                    <BandStatusBadge status={b.status} />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {b.city}{b.state ? `/${b.state}` : ''} • {b.short_description}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(b)} data-testid={`edit-band-${b.slug}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setConfirmDel(b)} data-testid={`delete-band-${b.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-4xl text-white">BANDAS</div>
          <p className="text-muted-foreground">Cadastro de bandas participantes.</p>
        </div>
        <Button data-testid="admin-bands-new-button" onClick={openNew} className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
          <Plus className="w-4 h-4 mr-1" /> Nova banda
        </Button>
      </div>

      {renderBandsList()}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? 'Editar banda' : 'Nova banda'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nome*" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="band-form-name" />
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="band-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="participante">Participante</SelectItem>
                  <SelectItem value="classificada">Classificada</SelectItem>
                  <SelectItem value="eliminada">Eliminada</SelectItem>
                  <SelectItem value="repescagem">Repescagem</SelectItem>
                  <SelectItem value="campea">Campã</SelectItem>
                  <SelectItem value="inativa">Inativa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Cidade" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <Field label="Estado (UF)" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
            <Field label="Instagram URL" value={form.instagram_url} onChange={(v) => setForm({ ...form, instagram_url: v })} />
            <Field label="YouTube URL" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} />
            <Field label="Spotify URL" value={form.spotify_url} onChange={(v) => setForm({ ...form, spotify_url: v })} />
            <Field label="Site" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} />
            <Field label="Logo URL" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} className="sm:col-span-2" />
            <div className="sm:col-span-2">
              <Label>Descrição curta</Label>
              <Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label>Descrição completa</Label>
              <Textarea rows={4} value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="band-form-submit" onClick={save} disabled={saving} className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir banda?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel?.name} será removida. Se já possui histórico, será apenas inativada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[var(--g66-red)] text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value, onChange, testId, className = '' }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testId} />
    </div>
  );
}
